export type EditorChatHistoryImage = {
  id: string;
  url: string;
  preview: string;
  label: string;
  kind?: 'image' | 'video';
  role?: 'image' | 'clip';
  segmentId?: string;
  purpose?: 'avatar' | 'photo';
};

export type EditorChatHistoryMsg = {
  id: string;
  role: 'user' | 'assistant';
  text: string;
  images?: EditorChatHistoryImage[];
  audio?: { url: string; name: string; duration: number } | null;
  modelLabel?: string;
  credits?: number;
  generationCredits?: number;
  generationKind?: 'video' | 'image' | 'audio';
  plan?: {
    title: string;
    scenes: Array<{ label: string; prompt: string; duration_seconds?: number; notes?: string }>;
  };
  planConfirmed?: boolean;
};

export type EditorChatTimelineClip = {
  id: string;
  label: string;
  type: string;
  sourceUrl: string;
  thumbnailUrl?: string;
  mediaAssetId?: string;
  generationContext?: string;
  start?: number;
  end?: number;
  duration?: number;
  originalDuration?: number;
  speed?: number;
  trimStart?: number;
  trimEnd?: number;
  volume?: number;
  muted?: boolean;
  fit?: 'contain' | 'cover';
  transitionOut?: 'none' | 'fade' | 'dissolve';
  brightnessCurve?: 'none' | 'in' | 'out' | 'both';
  brightnessCurveSeconds?: number;
  motion?: 'none' | 'zoom-in' | 'zoom-out' | 'pan-left' | 'pan-right';
};

export type EditorChatTimelineAudio = {
  id: string;
  name: string;
  sourceUrl: string;
  duration: number;
  startOffset: number;
  volume: number;
  muted?: boolean;
  loop?: boolean;
  speed?: number;
  kind?: 'voice' | 'sfx' | 'music' | 'import';
  trimStart?: number;
  trimEnd?: number;
  generationContext?: string;
};

export type EditorChatTimelineCaption = {
  id: string;
  text: string;
  startTime: number;
  endTime: number;
  isImpactPhrase: boolean;
  position?: 'top' | 'center' | 'bottom';
};

export type EditorChatThread = {
  id: string;
  createdAt: number;
  updatedAt: number;
  title: string;
  messages: EditorChatHistoryMsg[];
  timelineClips?: EditorChatTimelineClip[];
  timelineAudio?: EditorChatTimelineAudio[];
  timelineCaptions?: EditorChatTimelineCaption[];
};

type ScopeRecord = {
  scope: string;
  threads: EditorChatThread[];
};

const DB_NAME = 'seu-video-editor-chat';
const DB_VERSION = 1;
const STORE = 'scopes';
export const EDITOR_CHAT_HISTORY_MAX = 24;

function scopeKey(userId: string, orgId: string): string {
  return `${userId}:${orgId}`;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'scope' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error('IndexedDB unavailable'));
  });
}

function idbGet(scope: string): Promise<ScopeRecord | undefined> {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, 'readonly');
        const req = tx.objectStore(STORE).get(scope);
        req.onsuccess = () => resolve(req.result as ScopeRecord | undefined);
        req.onerror = () => reject(req.error);
        tx.oncomplete = () => db.close();
      }),
  );
}

function idbPut(record: ScopeRecord): Promise<void> {
  return openDb().then(
    (db) =>
      new Promise((resolve, reject) => {
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).put(record);
        tx.oncomplete = () => {
          db.close();
          resolve();
        };
        tx.onerror = () => reject(tx.error);
      }),
  );
}

function newThreadId(): string {
  return `ec-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function listEditorChatThreads(userId: string, orgId: string): Promise<EditorChatThread[]> {
  return idbGet(scopeKey(userId, orgId))
    .then((rec) => rec?.threads || [])
    .catch((err) => {
      console.warn('editor chat history list', err);
      return [];
    });
}

export async function upsertEditorChatThread(
  userId: string,
  orgId: string,
  thread: {
    id?: string;
    title: string;
    messages: EditorChatHistoryMsg[];
    timelineClips?: EditorChatTimelineClip[];
    timelineAudio?: EditorChatTimelineAudio[];
    timelineCaptions?: EditorChatTimelineCaption[];
  },
): Promise<EditorChatThread | null> {
  const scope = scopeKey(userId, orgId);
  const now = Date.now();
  try {
    const rec = await idbGet(scope);
    const threads = rec?.threads || [];
    const existing = thread.id ? threads.find((t) => t.id === thread.id) : undefined;
    const timelineClips = thread.timelineClips !== undefined
      ? thread.timelineClips
      : (existing?.timelineClips ?? []);
    const timelineAudio = thread.timelineAudio !== undefined
      ? thread.timelineAudio
      : (existing?.timelineAudio ?? []);
    const timelineCaptions = thread.timelineCaptions !== undefined
      ? thread.timelineCaptions
      : (existing?.timelineCaptions ?? []);
    const nextThread: EditorChatThread = existing
      ? { ...existing, title: thread.title || existing.title, messages: thread.messages, timelineClips, timelineAudio, timelineCaptions, updatedAt: now }
      : {
        id: newThreadId(),
        createdAt: now,
        updatedAt: now,
        title: thread.title || 'Novo chat',
        messages: thread.messages,
        timelineClips,
        timelineAudio,
        timelineCaptions,
      };
    const rest = threads.filter((t) => t.id !== nextThread.id);
    await idbPut({ scope, threads: [nextThread, ...rest].slice(0, EDITOR_CHAT_HISTORY_MAX) });
    return nextThread;
  } catch (err) {
    console.warn('editor chat history save', err);
    return null;
  }
}
